import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { get, post, ApiError } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { OrderDetailsDrawer } from '@/components/domain/OrderDetailsDrawer';
import { QueueLoadError } from '@/components/domain/QueueLoadError';
import {
  productNameOf, afterCutoff, latestOrderUnits, trackingDisplay,
  type TrackingDisplay,
} from '@/lib/ordering';
import {
  fetchAllSubmissions, submissionsPageUrl, truncationNotice,
  type LoadedSubmissions,
} from '@/lib/submission-pages';
import { tiersFromSubmission } from '@/lib/prize-tiers';
import { cn } from '@/lib/cn';

type PrizePayload = { products: Array<{ id: string; shortName?: string; displayName?: string }> };

// Every `ordered` row, not the first page of them. This tab used to request a
// flat limit of 500 against 812 live `ordered` docs (measured 2026-08-25), so
// 312 fundraisers — their tracking, their units, their totals — simply were not
// in the table, and nothing on screen said so.
const ORDERED_QUEUE_URL = submissionsPageUrl({ status: 'ordered' });

const FILTERS = ['all', 'delivered', 'in-transit', 'unfulfilled'] as const;
type Filter = (typeof FILTERS)[number];
const FILTER_LABEL: Record<Filter, string> = {
  all: 'All Orders', delivered: 'Delivered', 'in-transit': 'In Transit', unfulfilled: 'Unfulfilled',
};

function badgeTone(kind: TrackingDisplay['kind']): string {
  switch (kind) {
    case 'delivered': return 'bg-success/10 text-success';
    case 'in-transit': return 'bg-primary/10 text-primary';
    case 'shipped': return 'bg-primary/10 text-primary';
    default: return 'bg-bg text-muted';
  }
}

// Order Tracking — submissions with status=ordered. Port of admin-dashboard's
// Orders tab (cutoff 2026-01-08, latest-order units, tracking badges).
export default function Orders() {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  // The key stays the first-page URL so refreshSubmission()'s
  // `/api/submissions?` prefix match still revalidates this tab after a mutation.
  const { data, error, isLoading, mutate } = useSWR<LoadedSubmissions>(
    ORDERED_QUEUE_URL,
    (url: string) => fetchAllSubmissions(url, get),
  );
  const { data: prize } = useSWR<PrizePayload>('/api/prize-products', get);
  const names = (id?: string) => productNameOf(prize?.products || [], id);
  const incomplete = data ? truncationNotice(data) : null;

  const orders = useMemo(() => {
    const list = (data?.submissions || []).filter(afterCutoff);
    return list.sort((a, b) =>
      new Date(b.lastOrderAt || b.firstOrderAt || b.createdAt || 0).getTime() -
      new Date(a.lastOrderAt || a.firstOrderAt || a.createdAt || 0).getTime()
    );
  }, [data]);

  const stats = useMemo(() => {
    let delivered = 0;
    let inTransit = 0;
    let units = 0;
    for (const o of orders) {
      units += latestOrderUnits(o);
      const t = trackingDisplay(o);
      if (t.kind === 'delivered') delivered++;
      else if (t.kind === 'in-transit' || t.kind === 'shipped') inTransit++;
    }
    return { total: orders.length, units, delivered, inTransit };
  }, [orders]);

  const filtered = orders.filter((o) => {
    if (q.trim()) {
      const needle = q.toLowerCase();
      const hit =
        String(o.fundraiserName || '').toLowerCase().includes(needle) ||
        String(o.id || '').toLowerCase().includes(needle) ||
        String(o.adminName || '').toLowerCase().includes(needle);
      if (!hit) return false;
    }
    if (filter === 'all') return true;
    const t = trackingDisplay(o);
    if (filter === 'delivered') return t.kind === 'delivered';
    if (filter === 'in-transit') return t.kind === 'in-transit' || t.kind === 'shipped';
    return t.kind === 'unfulfilled';
  });

  const syncAll = async () => {
    setSyncingAll(true);
    toast.info('Syncing tracking for all orders — this can take a minute…');
    try {
      const r = await post<{ success: boolean; synced?: number; error?: string }>('/api/orders/sync-tracking-all', {});
      toast.success(`Tracking synced${typeof r.synced === 'number' ? ` for ${r.synced} orders` : ''}`);
      void mutate();
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { error?: string } | null) : null;
      toast.error(body?.error || (e as Error).message || 'Sync failed');
    } finally {
      setSyncingAll(false);
    }
  };

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Order Tracking</h1>
      <p className="mb-3 text-[12px] text-muted">
        View and track all fundraiser orders · {filtered.length} shown
        {data?.total != null && ` · ${data.total} ordered in total`}
      </p>

      {/* Only rendered when the queue is provably NOT whole. Silence here is a
          positive claim: every ordered fundraiser is in the table below. */}
      {incomplete && (
        <div className="mb-3 rounded-sm border border-warning/40 bg-warning/10 px-3 py-2 text-[12px] text-text/85">
          {incomplete}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by fundraiser, admin, or ID…" className="max-w-[280px]" />
        <div className="flex gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn('rounded-sm px-2.5 py-1 text-[11px]', filter === f ? 'bg-primary text-white' : 'border border-border bg-surface text-text/75 hover:bg-bg')}
            >
              {FILTER_LABEL[f]}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3 text-[11.5px] text-muted">
          <span><strong className="text-text">{stats.total}</strong> orders</span>
          <span><strong className="text-text">{stats.units.toLocaleString()}</strong> units</span>
          <span><strong className="text-success">{stats.delivered}</strong> delivered</span>
          <span><strong className="text-primary">{stats.inTransit}</strong> in transit</span>
          <Button variant="secondary" size="sm" disabled={syncingAll} onClick={syncAll}>
            {syncingAll ? 'Syncing…' : 'Sync All Tracking'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void mutate()}>Refresh</Button>
        </div>
      </div>

      <div className="rounded-md bg-surface shadow-card">
        {isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : error ? (
          <QueueLoadError queue="order tracking" onRetry={() => void mutate()} />
        ) : filtered.length ? (
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-subtle">
                <th className="px-3 py-2 font-semibold">Fundraiser</th>
                <th className="px-3 py-2 font-semibold">Admin</th>
                <th className="px-3 py-2 font-semibold">Products</th>
                <th className="px-3 py-2 font-semibold">Units</th>
                <th className="px-3 py-2 font-semibold">Orders</th>
                <th className="px-3 py-2 font-semibold">Tracking</th>
                <th className="px-3 py-2 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const t = trackingDisplay(o);
                const orderDate = o.lastOrderAt || o.firstOrderAt || o.createdAt;
                const orderCount = o.orderCount || (o.orders || []).length || 1;
                return (
                  <tr key={o.id} className="border-b border-border/50 hover:bg-bg/50">
                    <td className="px-3 py-2">
                      <div className="font-medium">{o.fundraiserName || '-'}</div>
                      <div className="font-mono text-[10px] text-subtle">{o.id}</div>
                    </td>
                    <td className="px-3 py-2">{o.adminName || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        {tiersFromSubmission(o).map((t) => (
                          <span key={t.index} className="w-fit rounded-sm bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">
                            {names(t.product || undefined)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-semibold">{latestOrderUnits(o)}</td>
                    <td className="px-3 py-2">
                      <div>{orderDate ? new Date(orderDate).toLocaleDateString() : '-'}</div>
                      <div className="text-[10.5px] text-subtle">{orderCount} order{orderCount !== 1 ? 's' : ''}</div>
                    </td>
                    <td className="px-3 py-2">
                      {t.url ? (
                        <a href={t.url} target="_blank" rel="noreferrer" className={cn('inline-block rounded-sm px-1.5 py-0.5 text-[11px] hover:underline', badgeTone(t.kind))} title={t.number ? `${t.company || 'Track'}: ${t.number}` : undefined}>
                          {t.text}
                        </a>
                      ) : (
                        <span className={cn('inline-block rounded-sm px-1.5 py-0.5 text-[11px]', badgeTone(t.kind))}>{t.text}</span>
                      )}
                      {t.number && !t.url && (
                        <div className="mt-0.5 max-w-[120px] truncate font-mono text-[9.5px] text-subtle" title={t.number}>{t.company ? `${t.company}: ` : ''}{t.number}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="secondary" size="sm" onClick={() => setDetailsId(o.id)}>Details</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-[12px] text-muted">
            {q || filter !== 'all' ? 'No matching orders.' : 'No orders found — orders appear here once placed.'}
          </div>
        )}
      </div>

      <OrderDetailsDrawer
        id={detailsId}
        onClose={() => setDetailsId(null)}
        onChanged={() => void mutate()}
        productNames={names}
      />
    </div>
  );
}
